import { createTheme, darken, Palette, PaletteOptions, Theme } from '@mui/material';
import createPalette from '@mui/material/styles/createPalette';
import React from 'react';

import CheckIcon from '@/assets/icons/check.svg?react';
import CheckboxCheckedDark from '@/assets/icons/checkbox-checked.dark.svg?react';
import CheckboxCheckedLight from '@/assets/icons/checkbox-checked.light.svg?react';
import CheckboxUncheckedDark from '@/assets/icons/checkbox-unchecked.dark.svg?react';
import CheckboxUncheckedLight from '@/assets/icons/checkbox-unchecked.light.svg?react';
import DropdownArrow from '@/assets/icons/dropdown-arrow.svg?react';
import ExclamationSign from '@/assets/icons/exclamation-sign.svg?react';
import InfoIcon from '@/assets/icons/info-icon.svg?react';
import RadioCheckedDark from '@/assets/icons/radio-checked.dark.svg?react';
import RadioCheckedLight from '@/assets/icons/radio-checked.light.svg?react';
import RadioUncheckedDark from '@/assets/icons/radio-unchecked.dark.svg?react';
import RadioUncheckedLight from '@/assets/icons/radio-unchecked.light.svg?react';
import { SortIcon } from '@/components/molecules/SortIcon';

const { breakpoints } = createTheme();

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    dark: true;
    light: true;
  }

  interface ButtonPropsSizeOverrides {
    compact: true;
  }
}

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsSizeOverrides {
    compact: true;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    h7: React.CSSProperties;
    h8: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    h7: React.CSSProperties;
    h8: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h7: true;
    h8: true;
  }
}

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    custom: {
      LightBlue: string;
      LightGray: string;
      DividerLight: string;
      DividerMed: string;
      Inactive: string;
      OffWhite: string;
      InputsBackground: string;
      White: string;
      BlackLightBackground: string;
      Primary4Background: string;
      Primary3Background: string;
      Primary1Background: string;
      AllPrimary3Background: string;
      InactiveBackground: string;
      BlackText: string;
      MenuBackground: string;
      AlternativeBackground: string;
      DefaultText: string;
      Skeleton: string;
    };
    status: {
      failed: {
        foreground: string;
        background: string;
      };
      completed: {
        foreground: string;
        background: string;
      };
      pending: {
        foreground: string;
        background: string;
      };
    };
  }

  interface PaletteOptions {
    custom: {
      LightBlue: string;
      LightGray: string;
      DividerLight: string;
      DividerMed: string;
      Inactive: string;
      OffWhite: string;
      InputsBackground: string;
      White: string;
      BlackLightBackground: string;
      Primary4Background: string;
      Primary3Background: string;
      Primary1Background: string;
      AllPrimary3Background: string;
      InactiveBackground: string;
      BlackText: string;
      MenuBackground: string;
      AlternativeBackground: string;
      DefaultText: string;
      Skeleton: string;
    };
    status: {
      failed: {
        foreground: string;
        background: string;
      };
      completed: {
        foreground: string;
        background: string;
      };
      pending: {
        foreground: string;
        background: string;
      };
    };
  }
}

const darkPalette: PaletteOptions = {
  mode: 'dark',
  primary: {
    main: '#a1bceb',
    dark: '#91acda',
    contrastText: '#1e1e1e',
  },
  secondary: {
    main: '#8A8A8A',
  },
  error: {
    main: '#dc4f6d',
    dark: '#cc2f3d',
    light: '#f5e5e5',
  },
  text: {
    primary: '#ecf0f4',
    secondary: '#B0B0B0',
    disabled: '#666666',
  },
  background: {
    paper: '#1E1E1E',
  },
  custom: {
    LightBlue: '#2A3A4A',
    LightGray: '#aaaaaa',
    DividerLight: '#505050',
    DividerMed: '#666666',
    Inactive: '#666666',
    OffWhite: '#2E2E2E',
    InputsBackground: '#2A2A2A',
    White: '#1E1E1E',
    BlackLightBackground: '#eeeeee',
    Primary4Background: '#4A90E2',
    Primary3Background: '#5A7FA0',
    Primary1Background: '#a1bceb10',
    AllPrimary3Background: '#2C3A47',
    InactiveBackground: '#3A3A3A',
    MenuBackground: '#0F1419',
    BlackText: '#FFFFFF',
    AlternativeBackground: '#262626',
    DefaultText: '#FFFFFFFF',
    Skeleton: 'rgba(255, 255, 255, 0.15)',
  },
  status: {
    failed: {
      foreground: '#ff4e5e',
      background: '#2c1215',
    },
    completed: {
      foreground: '#4caf5b',
      background: '#162a1b',
    },
    pending: {
      foreground: '#ff9f50',
      background: '#2c1d12',
    },
  },
};

const lightPalette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: '#0b53cf',
    dark: '#052761',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#808080',
  },
  error: {
    main: '#cc2f3d',
    light: '#f5e5e5',
  },
  text: {
    primary: '#0c1014',
    secondary: '#616161',
    disabled: '#939295',
  },
  background: {
    paper: '#fff',
  },
  custom: {
    LightBlue: '#e7eefa',
    LightGray: '#808080',
    DividerLight: '#e5e5e5',
    DividerMed: '#dddddd',
    Inactive: '#939295',
    OffWhite: '#fafaf8',
    InputsBackground: '#f0f0ee',
    White: '#ffffff',
    BlackLightBackground: '#222222',
    Primary4Background: '#0b53cf',
    Primary3Background: '#82a6e3',
    Primary1Background: '#ECF2FE',
    AllPrimary3Background: '#e2e9f4',
    InactiveBackground: '#dadadb',
    MenuBackground: '#011928',
    BlackText: '#000000',
    AlternativeBackground: '#f7f6f2',
    // WARNING: this is a strange trick needed to convince the MUI that we want the text to be black.
    //          Even using the #0c1013 turns it to the primary color :/
    DefaultText: '#0c1013FF',
    Skeleton: 'rgba(0, 0, 0, 0.15)',
  },
  status: {
    failed: {
      foreground: '#cc2f3d',
      background: '#f7d4d9',
    },
    completed: {
      foreground: '#3c834a',
      background: '#d4e6da',
    },
    pending: {
      foreground: '#dc7c30',
      background: '#f5e2d1',
    },
  },
};

const createDashboardTheme = (mode: 'dark' | 'light'): Theme => {
  const palette: Palette = createPalette({
    ...(mode === 'light' ? lightPalette : darkPalette),
  });

  return createTheme({
    palette: palette,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 1500,
        lg: 1700,
        xl: 1900,
      },
    },
    typography: {
      fontFamily: ['Lexend', 'sans-serif'].join(','),
      caption: {
        color: 'text.secondary',
        lineHeight: '20px',
        fontSize: 12,
      },
      body1: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '20px',
      },
      body2: {
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '24px',
      },
      subtitle1: {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: '24px',
      },
      h1: {
        fontSize: 56,
        lineHeight: '67.2px',
        [breakpoints.down('sm')]: {
          fontSize: 32,
          lineHeight: '40px',
        },
        fontWeight: 500,
      },
      h2: {
        fontSize: 40,
        lineHeight: '48px',
        [breakpoints.down('sm')]: {
          fontSize: 32,
          lineHeight: '40px',
        },
        fontWeight: 500,
      },
      h3: {
        fontSize: 32,
        lineHeight: '40px',
        [breakpoints.down('sm')]: {
          fontSize: 32,
          lineHeight: '40px',
        },
        fontWeight: 500,
      },
      h5: {
        color: 'text.primary',
        fontSize: 32,
        lineHeight: '40px',
        fontWeight: 500,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontSize: 24,
        lineHeight: '32px',
        fontWeight: 500,
      },
      h7: {
        fontFamily: ['Lexend', 'sans-serif'].join(','),
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 500,
      },
      h8: {
        fontFamily: ['Lexend', 'sans-serif'].join(','),
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '24px',
      },
    },
    components: {
      MuiDivider: {
        styleOverrides: {
          root: {
            backgroundColor: palette.custom.DividerLight,
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          elevation: 4,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          marginThreshold: 8,
          PaperProps: {
            style: { marginTop: 8 },
          },
          disablePortal: false,
          disableAutoFocus: true,
        },
        styleOverrides: {
          list: {
            maxHeight: '100%',
            overflow: 'auto',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            alignItems: 'center',
          },
          icon: {
            width: '20px',
            height: '20px',
            alignItems: 'center',
            padding: 0,
            '& svg': {
              flex: 1,
            },
          },
        },
        defaultProps: {
          iconMapping: {
            info: <InfoIcon fill="currentColor" />,
            warning: <ExclamationSign fill="currentColor" />,
            error: <ExclamationSign fill="currentColor" />,
            success: <CheckIcon fill="currentColor" />,
          },
        },
        variants: [
          {
            props: { severity: 'info' },
            style: { backgroundColor: palette.custom.LightBlue },
          },
          {
            props: { severity: 'warning' },
            style: { backgroundColor: palette.status.pending.background },
          },
          {
            props: { severity: 'error' },
            style: { backgroundColor: palette.status.failed.background },
          },
          {
            props: { severity: 'success' },
            style: { backgroundColor: palette.status.completed.background },
          },
        ],
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              backgroundColor: palette.custom.AlternativeBackground,
              opacity: 0.4,
              filter: 'grayscale(100%)',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            paddingLeft: 32,
            paddingRight: 32,
            paddingTop: 3,
            paddingBottom: 3,
          },
          expandIconWrapper: {
            color: palette.text.primary,
            svg: {
              fill: 'currentColor',
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            paddingLeft: 32,
            paddingRight: 32,
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
            position: 'absolute',
            width: 40,
            height: 40,
          },
          positionEnd: {
            right: 24,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: palette.custom.White,
            borderColor: palette.custom.DividerMed,
            borderWidth: 1,
            borderStyle: 'solid',
            boxShadow: '0px 1px 4px 0px #0C0C0D0D, 0px 1px 4px 0px #0C0C0D1A',
            fontSize: 14,
            fontWeight: 400,
            minWidth: 347,
            borderRadius: 8,
            color: palette.custom.BlackText,
            padding: 24,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          slotProps: {
            select: {
              MenuProps: {
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                marginThreshold: 8,
              },
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            '&:last-of-type': {
              borderColor: 'transparent',
            },
          },
          padding: {
            paddingTop: 8,
            paddingBottom: 8,
          },
          divider: {
            borderColor: palette.custom.DividerLight,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: 16,
            fontWeight: 500,
            lineHeight: '24px',
            fontFamily: 'Lexend',
            color: palette.text.primary,
          },
          secondary: {
            fontSize: 16,
            fontWeight: 400,
            lineHeight: '24px',
            fontFamily: 'Lexend',
            color: palette.custom.LightGray,
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            textAlign: 'center',
            width: 32,
            maxHeight: 'auto',
            '& svg': {
              minWidth: '100%',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginRight: 32,
            marginLeft: 32,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            marginTop: 6,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          elevation0: {
            backgroundColor: palette.custom.White,
            borderRadius: 16,
            boxShadow: 'none',
          },
          elevation1: {
            backgroundColor: palette.custom.OffWhite,
            borderRadius: 16,
            boxShadow: '0px 5px 40px 0px rgba(0, 0, 0, 0.03)',
          },
          elevation2: {
            backgroundColor: palette.custom.OffWhite,
            borderRadius: 16,
            boxShadow: '0px 5px 40px 0px rgba(0, 0, 0, 0.12)',
          },
          elevation3: {
            backgroundColor: palette.custom.White,
            borderRadius: 8,
            boxShadow: '0px 5px 40px 0px rgba(0, 0, 0, 0.03)',
          },
          elevation4: {
            backgroundColor: palette.custom.White,
            borderRadius: 24,
            boxShadow: '0px 5px 40px 0px rgba(0, 0, 0, 0.03)',
          },
          elevation5: {
            backgroundColor: palette.custom.White,
            borderRadius: 24,
            boxShadow:
              '0px 1px 4px 0px rgba(12, 12, 13, 0.05), 0px 1px 4px 0px rgba(12, 12, 13, 0.1)',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '& svg': {
              width: 24,
              height: 24,
            },
          },
          sizeSmall: {
            '& svg': {
              width: 17,
              height: 17,
            },
          },
        },
        defaultProps: {
          icon: mode === 'light' ? <CheckboxUncheckedLight /> : <CheckboxUncheckedDark />,
          checkedIcon: mode === 'light' ? <CheckboxCheckedLight /> : <CheckboxCheckedDark />,
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '& svg': {
              width: 24,
              height: 24,
            },
          },
        },
        defaultProps: {
          icon: mode === 'light' ? <RadioUncheckedLight /> : <RadioUncheckedDark />,
          checkedIcon: mode === 'light' ? <RadioCheckedLight /> : <RadioCheckedDark />,
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            pointerEvents: 'none',
            color: palette.custom.LightGray,
            lineHeight: '24px',
            marginTop: -12,
            transform: 'translate(32px, 36px)',
            fontSize: 16,
            '&.Mui-disabled': {
              color: palette.text.secondary,
            },
          },
          asterisk: {
            display: 'none',
          },
          shrink: {
            color: palette.custom.LightGray,
            transform: 'translate(32px, 26px)',
            lineHeight: '20px',
            fontSize: 12,
          },
        },
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: palette.custom.LightGray,
            '&.Mui-disabled': {
              opacity: 0.5,
            },
            '&.Mui-focused': {
              color: palette.custom.LightGray,
            },
            '&.Mui-error': {
              color: palette.custom.LightGray,
            },
          },
          colorSecondary: {
            color: palette.custom.LightGray,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            alignItems: 'flex-start',
            color: palette.custom.LightGray,
            '&.Mui-focused': {
              color: palette.custom.LightGray,
            },
            '&.Mui-error': {
              color: palette.custom.LightGray,
            },
          },
          label: {
            mt: 2,
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            color: palette.custom.LightGray,
            backgroundColor: 'transparent',
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 24,
            paddingRight: 24,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          dense: {
            paddingTop: 0,
            paddingBottom: 0,
            color: palette.text.primary,
          },
          root: {
            paddingLeft: 24,
            paddingRight: 24,
            paddingTop: 16,
            paddingBottom: 16,
            fontSize: 16,
            '&.Mui-selected': {
              backgroundColor: 'transparent',
            },
            '&.Mui-selected:hover, &:hover': {
              backgroundColor: palette.custom.InputsBackground,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            height: 72,
            paddingLeft: 32,
            paddingRight: 32,
          },
          icon: {
            top: '50%',
            fill: palette.text.primary,
            marginTop: -12,
            height: 24,
            width: 24,
            right: 24,
          },
          iconOpen: {
            marginTop: -13,
          },
        },
        defaultProps: {
          IconComponent: DropdownArrow,
          MenuProps: {
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            marginThreshold: 8,
            slotProps: {
              paper: {
                sx: { marginTop: 8 },
              },
            },
            disablePortal: false,
            disableAutoFocus: true,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          input: {
            height: 72,
            boxSizing: 'border-box',
            width: '100%',
            paddingTop: 32,
            paddingBottom: 16,
            paddingLeft: 32,
            paddingRight: 32,
            fontWeight: 400,
            lineHeight: '24px',
            fontSize: 16,
            borderRadius: 0,

            '&::placeholder': {
              color: palette.custom.LightGray,
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            height: 56,
            lineHeight: '56px',
          },
        },
      },
      MuiTableSortLabel: {
        defaultProps: {
          IconComponent: SortIcon,
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            height: 52,
            lineHeight: '52px',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 0,
            paddingBottom: 0,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            borderColor: palette.custom.DividerLight,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            overflow: 'hidden',
            backgroundColor: palette.custom.InputsBackground,
            borderRadius: 24,
            '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.custom.BlackLightBackground,
              borderWidth: 1,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: palette.error.main,
              borderWidth: 1,
            },
            '&.Mui-disabled': {
              background: palette.custom.InactiveBackground,
            },
          },
          multiline: {
            height: 'auto',
            padding: 0,
            paddingTop: 2,
          },
          input: {
            height: 72,
            boxSizing: 'border-box',
            width: '100%',
            paddingTop: 32,
            paddingBottom: 16,
            paddingLeft: 32,
            paddingRight: 32,
            fontWeight: 400,
            lineHeight: '24px',
            fontSize: 16,
            borderRadius: 0,
            '&::placeholder': {
              color: palette.custom.LightGray,
            },
            '& .Mui-disabled': {
              // color: palette.custom.Inactive,
              WebkitTextFillColor: palette.text.primary,
            },
          },
          adornedEnd: {
            paddingRight: 0,
          },
          notchedOutline: {
            borderColor: palette.custom.DividerLight,
            borderWidth: 1,
            '& legend': {
              width: 0,
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            cursor: 'pointer',
          },
        },
        defaultProps: {
          fontSize: 14,
          fontWeight: 500,
          underline: 'none',
          color: 'text.primary',
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              opacity: 0.4,
            },
          },
        },
      },
      MuiButton: {
        variants: [
          {
            props: {
              variant: 'dark',
            },
            style: {
              backgroundColor: palette.custom.BlackLightBackground,
              color: palette.primary.contrastText,
              '&.Mui-disabled': {
                color: 'white',
                borderColor: 'unset',
                opacity: 0.25,
              },
              '&:hover': {
                backgroundColor: darken(palette.custom.BlackLightBackground, 0.15),
              },
            },
          },
          {
            props: {
              variant: 'light',
            },
            style: {
              backgroundColor: palette.custom.White,
              borderWidth: 1.5,
              color: palette.text.primary,
              borderColor: palette.custom.DividerLight,
              borderStyle: 'solid',
              '&:hover': {
                backgroundColor: palette.custom.OffWhite,
              },
              '&:active': {
                borderColor: palette.custom.DividerMed,
              },
              '& .MuiTouchRipple-root': {
                color: palette.custom.DividerMed,
              },
            },
          },
        ],
        styleOverrides: {
          root: {
            borderRadius: 28,
            textTransform: 'none',
            fontSize: 16,
            fontWeight: 400,
            height: 56,
            padding: '0 32px',
            '&.MuiButton-sizeCompact': {
              height: 40,
              padding: '16px 24px',
            },
            '&.Mui-disabled': {
              color: 'unset',
              borderColor: 'unset',
              opacity: 0.5,
            },
            '&.MuiButton-outlined.MuiButton-colorError.Mui-disabled': {
              color: palette.error.main,
              borderColor: palette.error.main,
            },
          },
          sizeSmall: {
            height: 32,
            borderRadius: 24,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          text: {
            width: 'fit-content',
            padding: 0,
            height: 'auto',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderRadius: 28,
            width: 56,
            height: 56,
            zIndex: 'auto',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            willChange: 'contents',
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            transition: 'none',
          },
          invisible: {
            transition: 'none',
            backdropFilter: 'none',
            background: 'none',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            position: 'relative',
            '&::after': {
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              content: '"-"',
              height: 1,
              backgroundColor: palette.custom.DividerMed,
            },
          },
          list: {
            marginBottom: 0,
          },
          indicator: {
            height: 3,
            zIndex: 1,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: 16,
            lineHeight: '24px',
            height: 56,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.custom.OffWhite,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            maxWidth: 744,
            minWidth: 552,
          },
          paperFullWidth: {
            maxWidth: 'unset',
            minWidth: 'unset',
          },
          paperFullScreen: {
            maxWidth: 'unset',
            minWidth: 'unset',
          },
        },
        defaultProps: {
          slotProps: {
            paper: {
              elevation: 1,
            },
          },
        },
      },
    },
  });
};

export default createDashboardTheme;
